import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Button } from '@components';
import { Card, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { messageToast } from '../../utils/define';
import { addNhiemVuKhacAction } from '../../store/reducers/nhiemvukhac';
import { nhanVienGetAllAction } from '../../store/reducers/nhanVien';
import { getNguoiDungByAccountId } from '../../store/reducers/nguoidung';
import { userManager } from '@app/utils/oidc';
import { getInfomationAccountFromToken } from '../../utils/helper';
import { MUC_DO_UU_TIEN, TRANG_THAI_KHAC_OPTIONS } from '../../store/constants';

const schema = yup.object().shape({
  title: yup.string().required('Vui lòng nhập tiêu đề'),
  moTa: yup.string(),
  mucDo: yup
    .number()
    .typeError('Vui lòng chọn mức độ')
    .required('Vui lòng chọn mức độ'),
  hanChot: yup.string().nullable(), // không bắt buộc
  ngayGiao: yup.string().nullable(),
  ngayKetThucThucTe: yup.string().nullable(),
  nhanVienId: yup
    .number()
    .typeError('Vui lòng chọn nhân viên')
    .required('Vui lòng chọn nhân viên'),
});

const getDefaultMucDoBinhThuong = () => {
  if (!Array.isArray(MUC_DO_UU_TIEN) || MUC_DO_UU_TIEN.length === 0) return '';

  const normalize = (s) => {
    if (!s) return '';
    try {
      return s
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
    } catch {
      return s.toString().toLowerCase().trim();
    }
  };

  const target = normalize('Bình thường');

  let found =
    MUC_DO_UU_TIEN.find(
      (opt) =>
        normalize(opt.label) === target ||
        normalize(opt.key) === target
    ) ||
    MUC_DO_UU_TIEN.find((opt) => opt.default) ||
    MUC_DO_UU_TIEN[0];

  return typeof found?.value === 'number' || typeof found?.value === 'string'
    ? found.value
    : '';
};

const getTodayStr = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const NhiemVuKhacAdd = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const listNhanVien = useSelector((state) => state.nhanVien.list || []);

  const [isLoading, setIsLoading] = useState(false);

  const [nhanVienKeyword, setNhanVienKeyword] = useState('');

  const getNhanVienLabel = (nv) =>
    nv?.tenNhanVien || nv?.hoTen || nv?.name || nv?.ten || nv?.displayName || '';

  const normalizeText = (s) => {
    if (!s) return '';
    try {
      return s
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
    } catch {
      return s.toString().toLowerCase().trim();
    }
  };

  const filteredNhanVien = useMemo(() => {
    const kw = normalizeText(nhanVienKeyword);
    if (!kw) return listNhanVien;

    return (listNhanVien || []).filter((nv) => {
      const name = normalizeText(getNhanVienLabel(nv));
      return name.includes(kw);
    });
  }, [listNhanVien, nhanVienKeyword]);

  const [nvOpen, setNvOpen] = useState(false);
  const [nvActiveIndex, setNvActiveIndex] = useState(-1);
  const nvBoxRef = useRef(null);
  const nvMenuRef = useRef(null);

  useEffect(() => {
    if (!nvOpen) return;
    const menu = nvMenuRef.current;
    if (!menu) return;

    const activeEl = menu.querySelector('.dropdown-item.active');
    if (activeEl) activeEl.scrollIntoView({ block: 'nearest' });
  }, [nvOpen, nvActiveIndex]);

  // click ra ngoài -> đóng dropdown
  useEffect(() => {
    const onMouseDown = (e) => {
      if (!nvBoxRef.current) return;
      if (!nvBoxRef.current.contains(e.target)) {
        setNvOpen(false);
        setNvActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const selectNhanVien = (nv, setFieldValue) => {
    if (!nv) return;
    setFieldValue('nhanVienId', Number(nv.id));
    setNhanVienKeyword(getNhanVienLabel(nv)); // input hiển thị tên nhân viên đã chọn
    setNvOpen(false);
    setNvActiveIndex(-1);
  };

  const handleNhanVienInputChange = (e, setFieldValue) => {
    const v = e.target.value;
    setNhanVienKeyword(v);

    // đang gõ => chưa chọn ai => reset nhanVienId để yup bắt buộc chọn đúng
    setFieldValue('nhanVienId', '');

    setNvOpen(true);
    setNvActiveIndex(0);
  };

  const handleNhanVienKeyDown = (e, setFieldValue) => {
    if (!nvOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setNvOpen(true);
      setNvActiveIndex(0);
      return;
    }
    if (!nvOpen) return;

    if (e.key === 'Escape') {
      setNvOpen(false);
      setNvActiveIndex(-1);
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setNvActiveIndex((prev) => {
        const next = prev + 1;
        return next >= filteredNhanVien.length ? filteredNhanVien.length - 1 : next;
      });
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setNvActiveIndex((prev) => (prev - 1 < 0 ? 0 : prev - 1));
      return;
    }

    if (e.key === 'Enter') {
      if (nvActiveIndex >= 0 && nvActiveIndex < filteredNhanVien.length) {
        e.preventDefault();
        selectNhanVien(filteredNhanVien[nvActiveIndex], setFieldValue);
      }
    }
  };

  const [accountId, setAccountId] = useState(null);

  const [mappedNguoiDungId, setMappedNguoiDungId] = useState(null);
  const [mappedNguoiDungName, setMappedNguoiDungName] = useState(null);
  const [accountDisplayName, setAccountDisplayName] = useState(null);

  // files
  const [newFiles, setNewFiles] = useState([]);
  const fileInputRef = useRef(null);

  const toFileKey = (f) => `${f.name}__${f.size}__${f.lastModified}`;

  const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let v = bytes;
    let i = 0;
    while (v >= 1024 && i < units.length - 1) {
      v = v / 1024;
      i += 1;
    }
    return `${v.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const user = await userManager.getUser();
        const idFromUser =
          user?.profile?.sub ||
          user?.profile?.accountId ||
          user?.profile?.nameid ||
          null;
        const nameFromUser =
          user?.profile?.name ||
          user?.profile?.preferred_username ||
          user?.profile?.email ||
          null;

        if (mounted && idFromUser) {
          setAccountId(idFromUser);
          if (nameFromUser) setAccountDisplayName(nameFromUser);

          try {
            const payload = await dispatch(
              getNguoiDungByAccountId(idFromUser)
            ).unwrap();
            let item = payload || null;
            if (Array.isArray(item)) item = item[0] || null;
            if (item && item.data) item = item.data;
            if (Array.isArray(item)) item = item[0] || null;

            const foundId = item?.id || item?.nguoiDungId || null;
            const foundName =
              item?.hoTen || item?.fullName || item?.name || item?.ten || null;

            if (foundId) setMappedNguoiDungId(foundId);
            if (foundName) setMappedNguoiDungName(foundName);
          } catch (_) {}

          return;
        }
      } catch (_) {}

      try {
        const info = getInfomationAccountFromToken();
        const idFromToken =
          info?.sub || info?.accountId || info?.nameid || null;
        const nameFromToken =
          info?.name || info?.preferred_username || info?.email || null;

        if (mounted && idFromToken) {
          setAccountId(idFromToken);
          if (nameFromToken) setAccountDisplayName(nameFromToken);

          try {
            const payload = await dispatch(
              getNguoiDungByAccountId(idFromToken)
            ).unwrap();
            let item = payload || null;
            if (Array.isArray(item)) item = item[0] || null;
            if (item && item.data) item = item.data;
            if (Array.isArray(item)) item = item[0] || null;

            const foundId = item?.id || item?.nguoiDungId || null;
            const foundName =
              item?.hoTen || item?.fullName || item?.name || item?.ten || null;

            if (foundId) setMappedNguoiDungId(foundId);
            if (foundName) setMappedNguoiDungName(foundName);
          } catch (_) {}
        }
      } catch (_) {}
    })();

    return () => {
      mounted = false;
    };
  }, [dispatch]);

  useEffect(() => {
    if (!listNhanVien.length) dispatch(nhanVienGetAllAction());
  }, [dispatch, listNhanVien.length]);

  const handlePickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;

    setNewFiles((prev) => {
      const map = new Map(prev.map((f) => [toFileKey(f), f]));
      picked.forEach((f) => map.set(toFileKey(f), f));
      return Array.from(map.values());
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeNewFile = (idx) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleClearNewFiles = () => {
    setNewFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCreate = async (values) => {
    const nguoiDungId = mappedNguoiDungId;
    if (!nguoiDungId) {
      toast.error(
        'Không xác định được NguoiDungId (map từ account). Vui lòng đăng nhập lại hoặc kiểm tra mapping NguoiDung.'
      );
      return;
    }

    const payload = {
      data: {
        Title: values.title,
        MoTa: values.moTa,
        MucDo:
          values.mucDo === '' || values.mucDo === null ? null : Number(values.mucDo),
        NgayGiao: values.ngayGiao || null,
        HanChot: values.hanChot || null,
        NgayKetThucThucTe: values.ngayKetThucThucTe || null,

        Status: (values.status === '' || values.status === null)
          ? 0
          : Number(values.status),

        NguoiDungId: nguoiDungId,
        NhanVienId: Number(values.nhanVienId),
      },
      files: newFiles,
    };

    try {
      setIsLoading(true);
      const status = await dispatch(addNhiemVuKhacAction(payload)).unwrap();
      if (status >= 200 && status < 300) {
        toast.success(messageToast.messageToastCreateSuccess);
        history.goBack();
      } else {
        toast.error(messageToast.messageToastCreateError);
      }
    } catch (err) {
      toast.error(err?.message || 'Đã có lỗi xảy ra, vui lòng thử lại!');
    } finally {
      setIsLoading(false);
    }
  };

  const initialNguoiTaoDisplay =
    mappedNguoiDungName || accountDisplayName || '';

  const defaultMucDo = getDefaultMucDoBinhThuong();
  const defaultNgayGiao = getTodayStr();

  return (
    <Card>
      <Card.Header as="h5">Thêm mới Nhiệm vụ khác</Card.Header>
      <Card.Body>
        <Formik
          validationSchema={schema}
          onSubmit={handleCreate}
          initialValues={{
            title: '',
            moTa: '',
            mucDo: defaultMucDo,
            ngayGiao: defaultNgayGiao,
            hanChot: '',
            ngayKetThucThucTe: '',
            nhanVienId: '',
            status: (TRANG_THAI_KHAC_OPTIONS?.[0]?.value ?? 0),
          }}
        >
          {({ handleSubmit, handleChange, values, errors, setFieldValue }) => (
            <Form noValidate onSubmit={handleSubmit}>
              {/* Tiêu đề */}
              <Form.Group as={Row} className="mb-3">
                <Form.Label className="col-sm-2 col-form-label">
                  Tiêu đề
                </Form.Label>
                <Col sm="10">
                  <Form.Control
                    type="text"
                    name="title"
                    value={values.title}
                    onChange={handleChange}
                    isInvalid={!!errors.title}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.title}
                  </Form.Control.Feedback>
                </Col>
              </Form.Group>

              {/* Mô tả */}
              <Form.Group as={Row} className="mb-3">
                <Form.Label className="col-sm-2 col-form-label">
                  Mô tả
                </Form.Label>
                <Col sm="10">
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="moTa"
                    value={values.moTa}
                    onChange={handleChange}
                  />
                </Col>
              </Form.Group>

              {/* Mức độ */}
              <Form.Group as={Row} className="mb-3">
                <Form.Label className="col-sm-2 col-form-label">
                  Mức độ
                </Form.Label>
                <Col sm="10">
                  <Form.Control
                    as="select"
                    name="mucDo"
                    value={values.mucDo}
                    onChange={(e) =>
                      setFieldValue(
                        'mucDo',
                        e.target.value === '' ? '' : Number(e.target.value)
                      )
                    }
                    isInvalid={!!errors.mucDo}
                  >
                    <option value="">-- Chọn mức độ --</option>
                    {MUC_DO_UU_TIEN.map((opt) => (
                      <option key={opt.key} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    {errors.mucDo}
                  </Form.Control.Feedback>
                </Col>
              </Form.Group>

                          <Form.Group as={Row} className="mb-3">
                              {/* Label chung */}
                              <Form.Label column sm={2}>
                                  Thời gian
                              </Form.Label>

                              {/* 3 input date */}
                              <Col sm={10}>
                                  <Row>
                                      {/* Ngày giao */}
                                      <Col md={4}>
                                          <Form.Group>
                                              <Form.Label className="small text-muted">
                                                  Ngày giao
                                              </Form.Label>
                                              <Form.Control
                                                  type="date"
                                                  name="ngayGiao"
                                                  value={values.ngayGiao}
                                                  onChange={handleChange}
                                                  isInvalid={!!errors.ngayGiao}
                                              />
                                              <Form.Control.Feedback type="invalid">
                                                  {errors.ngayGiao}
                                              </Form.Control.Feedback>
                                          </Form.Group>
                                      </Col>

                                      {/* Hạn chót */}
                                      <Col md={4}>
                                          <Form.Group>
                                              <Form.Label className="small text-muted">
                                                  Hạn chót
                                              </Form.Label>
                                              <Form.Control
                                                  type="date"
                                                  name="hanChot"
                                                  value={values.hanChot}
                                                  onChange={handleChange}
                                                  isInvalid={!!errors.hanChot}
                                              />
                                              <Form.Control.Feedback type="invalid">
                                                  {errors.hanChot}
                                              </Form.Control.Feedback>
                                          </Form.Group>
                                      </Col>

                                      {/* Ngày hoàn thành thực tế */}
                                      <Col md={4}>
                                          <Form.Group>
                                              <Form.Label className="small text-muted">
                                                  Ngày hoàn thành thực tế
                                              </Form.Label>
                                              <Form.Control
                                                  type="date"
                                                  name="ngayKetThucThucTe"
                                                  value={values.ngayKetThucThucTe}
                                                  onChange={handleChange}
                                                  isInvalid={!!errors.ngayKetThucThucTe}
                                              />
                                              <Form.Control.Feedback type="invalid">
                                                  {errors.ngayKetThucThucTe}
                                              </Form.Control.Feedback>
                                          </Form.Group>
                                      </Col>
                                  </Row>
                              </Col>
                          </Form.Group>


              {/* Nhân viên */}
              <Form.Group as={Row} className="mb-3">
                <Form.Label className="col-sm-2 col-form-label">
                  Nhân viên
                </Form.Label>

                <Col sm="10">
                  <div ref={nvBoxRef} style={{ position: 'relative' }}>
                    <Form.Control
                      type="text"
                      placeholder="Chọn nhân viên..."
                      value={nhanVienKeyword}
                      onFocus={() => {
                        setNvOpen(true);
                        setNvActiveIndex(filteredNhanVien.length > 0 ? 0 : -1);
                      }}
                      onClick={() => {
                        setNvOpen(true);
                        setNvActiveIndex(filteredNhanVien.length > 0 ? 0 : -1);
                      }}
                      onChange={(e) => handleNhanVienInputChange(e, setFieldValue)}
                      onKeyDown={(e) => handleNhanVienKeyDown(e, setFieldValue)}
                      disabled={isLoading}
                      isInvalid={!!errors.nhanVienId}
                    />

                    {nvOpen && (
                      <div
                        ref={nvMenuRef}
                        className="dropdown-menu show w-100"
                        style={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          top: 'calc(100% + 4px)',
                          zIndex: 20,
                          maxHeight: 260,
                          overflowY: 'auto',
                        }}
                      >
                        {filteredNhanVien.length === 0 ? (
                          <div className="dropdown-item text-muted small">
                            Không tìm thấy nhân viên phù hợp.
                          </div>
                        ) : (
                          filteredNhanVien.map((nv, idx) => {
                            const active = idx === nvActiveIndex;
                            return (
                              <button
                                type="button"
                                key={nv.id}
                                className={`dropdown-item ${active ? 'active' : ''}`}
                                onMouseEnter={() => setNvActiveIndex(idx)} 
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  selectNhanVien(nv, setFieldValue);
                                }}
                              >
                                {getNhanVienLabel(nv)}
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}

                    <Form.Control.Feedback type="invalid">
                      {errors.nhanVienId}
                    </Form.Control.Feedback>
                  </div>
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3">
                <Form.Label className="col-sm-2 col-form-label">
                  Trạng thái
                </Form.Label>

                <Col sm="10">
                  <Form.Control
                    as="select"
                    name="status"
                    value={values.status}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFieldValue('status', v === '' ? 0 : Number(v));
                    }}
                    disabled={isLoading}
                  >
                    {(TRANG_THAI_KHAC_OPTIONS || []).map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Control>
                </Col>
              </Form.Group>

              {/* Files */}
              <Form.Group as={Row} className="mb-3">
                <Form.Label className="col-sm-2 col-form-label">
                  Tệp đính kèm
                </Form.Label>
                <Col sm="10">
                  <Form.Control
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handlePickFiles}
                    disabled={isLoading}
                  />

                  {newFiles.length > 0 && (
                    <Row className="mt-2">
                      <Col md={6}>
                        <div className="border rounded">
                          <div className="d-flex justify-content-between align-items-center px-2 py-1 border-bottom">
                            <div className="text-muted">
                              Đã chọn {newFiles.length} file
                            </div>
                            <Button
                              type="button"
                              theme="soft"
                              onClick={handleClearNewFiles}
                              disabled={isLoading}
                            >
                              Bỏ tất cả
                            </Button>
                          </div>

                          <div className="px-2 pb-1">
                            {newFiles.map((f, idx) => (
                              <div
                                key={toFileKey(f)}
                                className={`d-flex justify-content-between align-items-center py-2 ${
                                  idx < newFiles.length - 1
                                    ? 'border-bottom'
                                    : ''
                                }`}
                              >
                                <div className="me-3" style={{ minWidth: 0 }}>
                                  <div className="fw-semibold text-truncate">
                                    {f.name}
                                  </div>
                                  <div className="text-muted small">
                                    {formatBytes(f.size)}
                                  </div>
                                </div>

                                <Button
                                  type="button"
                                  theme="danger"
                                  onClick={() => removeNewFile(idx)}
                                  disabled={isLoading}
                                  style={{
                                    padding: '2px 10px',
                                    fontSize: 12,
                                    lineHeight: '18px',
                                  }}
                                >
                                  Xóa
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  )}
                </Col>
              </Form.Group>

              {/* Buttons */}
              <Form.Group as={Row} className="mb-3">
                <Col md={{ span: 10, offset: 2 }}>
                  <ButtonGroup>
                    <Button
                      type="submit"
                      theme="primary"
                      disabled={isLoading}
                    >
                      Thêm
                    </Button>
                    &nbsp;&nbsp;&nbsp;
                    <Button
                      theme="secondary"
                      onClick={() => history.goBack()}
                      disabled={isLoading}
                    >
                      Hủy
                    </Button>
                  </ButtonGroup>
                </Col>
              </Form.Group>
            </Form>
          )}
        </Formik>
      </Card.Body>
    </Card>
  );
};

export default NhiemVuKhacAdd;
